import React, { useState, useEffect, useRef } from 'react';
import api, { getGEXAnalysis, getInterpretation } from '../api';
import { GammaLensLogo } from './LandingPage';
import TickerSelector, { getTickerName } from './TickerSelector';
import GEXChart from './GEXChart';
import GEXHeatmap from './GEXHeatmap';
import AnnouncementBanner from './AnnouncementBanner';
import PriceChart from './PriceChart';
import useInstitutionalConfirmation from './useInstitutionalConfirmation';
import { usePostHog } from '@posthog/react';
import PCOIChart from './PCOIChart';
import FeedbackModal, { FeedbackBanner } from './FeedbackModal';

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://gammalens-api.onrender.com'
  : `http://${window.location.hostname}:8001`;



/* ─── Hooks ─── */
function useScrollReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function useCountUp(target, duration = 800, decimals = 0, active = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || target == null) return;
    const num = parseFloat(target);
    if (isNaN(num)) { setVal(target); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setVal(parseFloat((num * ease).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
      else setVal(parseFloat(num.toFixed(decimals)));
    };
    requestAnimationFrame(step);
  }, [target, duration, decimals, active]);
  return val;
}

/* ─── AnimatedProgressBar ─── */
function AnimBar({ pct, color, delay = 0, active }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => setW(pct), delay);
    return () => clearTimeout(t);
  }, [pct, active, delay]);
  return (
    <div style={{ height: '3px', background: 'rgba(255,255,255,0.04)', borderRadius: '2px', overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${w}%`, borderRadius: '2px',
        background: color,
        transition: `width 1.1s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }} />
    </div>
  );
}

/* ─── Level Card ─── */
function LevelCard({ label, sublabel, value, displayValue, color, pct, icon, confirmed, active, delay = 0 }) {
  const [ref, visible] = useScrollReveal();
  const counted = useCountUp(displayValue ? null : value, 850, 0, active && visible);
  const countedPct = useCountUp(pct ? Math.abs(parseFloat(pct)) : 0, 750, 1, active && visible);
  const barPct = pct ? Math.min(Math.abs(parseFloat(pct)) * 9, 100) : 60;

  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(18px)',
      transition: `opacity 0.4s ease ${delay}ms, transform 0.4s ease ${delay}ms`,
    }}>
      <div
        style={{
          background: '#1a1a1f',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '12px',
          padding: '16px',
          transition: 'background 0.15s ease',
          cursor: 'default',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#222228'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#1a1a1f'; }}
      >
        {/* Label row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span style={{ fontSize: '11px', color: '#8e8e93', fontWeight: '500', letterSpacing: '0.02em' }}>{label}</span>
          </div>
          {pct !== undefined && pct !== null && (
            <span style={{
              fontSize: '11px', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace",
              color: Number(pct) >= 0 ? '#30d158' : '#ff453a',
            }}>
              {Number(pct) >= 0 ? '+' : ''}{active && visible ? countedPct : '0.0'}%
            </span>
          )}
        </div>

        {/* Value */}
        <div style={{
          fontSize: '26px', fontWeight: '700', color: '#ffffff',
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '-0.5px', marginBottom: '12px', lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {displayValue || (active && visible ? `$${counted}` : '—')}
        </div>

        {/* Sublabel + confirmed */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '12px', color: '#8e8e93' }}>{sublabel}</span>
          {confirmed && (
            <span title="Confirmed by SPX institutional options positioning" style={{
              fontSize: '10px', fontWeight: 600, color: '#30d158', cursor: 'help',
            }}>✓ Inst.</span>
          )}
        </div>

        <AnimBar pct={barPct} color={color} delay={delay + 200} active={active && visible} />
      </div>
    </div>
  );
}

/* ─── FadeReveal ─── */
function FadeReveal({ children, delay = 0 }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(22px)',
      transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

/* ─── SectionHeader ─── */
function SectionHeader({ label, ticker, badge, gradient }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
      <span style={{ fontSize: '11px', color: '#aeaeb2', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span style={{ fontSize: '11px', color: '#71717a', fontFamily: "'JetBrains Mono', monospace" }}>·</span>
      <span style={{ fontSize: '11px', color: '#636366', fontFamily: "'JetBrains Mono', monospace" }}>{ticker}</span>
      <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.05)' }} />
      {badge}
    </div>
  );
}

/* ─── CopyLevelsButton ───────────────────────────────────────
   Copies the current ticker's gamma levels to the clipboard as
   a bare-number, one-per-line block that pastes cleanly into
   TradingView indicators, notes, spreadsheets, etc.

   Data source: everything already fetched by /api/gex/{ticker}.
   No backend work — GEX 1..N are derived here from data.strikes
   by ranking |net_gex| and excluding the CW/PW/Flip anchors so
   they don't duplicate.

   Copy path: prefers navigator.clipboard.writeText (secure
   contexts only); falls back to a hidden-textarea execCommand
   dance for iOS Safari / http contexts / older browsers.
─────────────────────────────────────────────────────────── */
function CopyLevelsButton({ ticker, levels, strikes }) {
  const [state, setState] = useState('idle'); // 'idle' | 'copied' | 'failed'
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Bare-number formatter for TradingView-style indicator inputs:
  //   integer  → no decimals  ("6015")
  //   fraction → up to 2 dp, trailing zeros trimmed  ("668.5", "668.42")
  //   null / non-finite → null (caller omits the line)
  const fmt = (v) => {
    if (v == null) return null;
    const n = Number(v);
    if (!isFinite(n)) return null;
    if (Number.isInteger(n)) return String(n);
    return String(Math.round(n * 100) / 100);
  };

  const buildText = () => {
    const lines = [`GammaLens ${ticker} levels`];
    const spot = fmt(levels?.spot_price);
    const cw = fmt(levels?.call_wall);
    const pw = fmt(levels?.put_wall);
    const gf = fmt(levels?.gamma_flip);
    if (spot != null) lines.push(`Spot: ${spot}`);
    if (cw != null) lines.push(`Call Wall: ${cw}`);
    if (pw != null) lines.push(`Put Wall: ${pw}`);
    if (gf != null) lines.push(`Gamma Flip: ${gf}`);

    // Rank remaining strikes by |net_gex|, drop the ones already surfaced as
    // CW/PW/Flip so we don't repeat them, cap at 5 secondary levels.
    const anchors = new Set(
      [levels?.call_wall, levels?.put_wall, levels?.gamma_flip]
        .filter(x => x != null)
        .map(Number)
    );
    const secondary = (strikes || [])
      .filter(s => s && s.net_gex && !anchors.has(Number(s.strike)))
      .sort((a, b) => Math.abs(b.net_gex) - Math.abs(a.net_gex))
      .slice(0, 5);
    secondary.forEach((s, i) => {
      const v = fmt(s.strike);
      if (v != null) lines.push(`GEX ${i + 1}: ${v}`);
    });

    return lines.join('\n');
  };

  const flashState = (next) => {
    setState(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState('idle'), 2000);
  };

  const legacyCopy = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleClick = async () => {
    const text = buildText();
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        flashState('copied');
        return;
      } catch {
        // fall through to legacy path
      }
    }
    flashState(legacyCopy(text) ? 'copied' : 'failed');
  };

  const isCopied = state === 'copied';
  const isFailed = state === 'failed';
  const label = isCopied ? 'Copied ✓' : isFailed ? 'Copy failed' : 'Copy levels';
  const color = isCopied ? '#30d158' : isFailed ? '#ff453a' : '#d4d4d8';
  const bg = isCopied ? 'rgba(48,209,88,0.10)' : isFailed ? 'rgba(255,69,58,0.10)' : 'rgba(255,255,255,0.09)';
  const border = isCopied ? 'rgba(48,209,88,0.28)' : isFailed ? 'rgba(255,69,58,0.28)' : 'rgba(255,255,255,0.22)';

  return (
    <button onClick={handleClick} aria-label="Copy levels to clipboard" title="Copy gamma levels as bare numbers" style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      background: bg, border: `1px solid ${border}`,
      borderRadius: '8px', padding: '6px 12px',
      color, fontSize: '10px', fontWeight: 700,
      fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em',
      cursor: 'pointer', minHeight: '30px',
      transition: 'all 0.2s ease',
      textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}
      onMouseEnter={e => { if (state === 'idle') { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.20)'; } }}
      onMouseLeave={e => { if (state === 'idle') { e.currentTarget.style.background = bg; e.currentTarget.style.color = color; e.currentTarget.style.borderColor = border; } }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
      <span>{label}</span>
    </button>
  );
}

/* ══════════════════════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════════════════════ */
function PWAInstallBanner() {
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('gl_pwa_dismissed')) return false;
    if (window.matchMedia('(display-mode: standalone)').matches) return false;
    if (window.navigator.standalone === true) return false;
    const ua = navigator.userAgent || '';
    return /Android|iPhone|iPad|iPod/i.test(ua) || (window.innerWidth <= 768);
  });

  if (!show) return null;

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  return (
    <div style={{
      marginBottom: '12px', padding: '12px 16px', borderRadius: '10px',
      background: 'linear-gradient(135deg, rgba(55,138,221,0.08), rgba(55,138,221,0.03))',
      border: '1px solid rgba(55,138,221,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <span style={{ fontSize: '18px', flexShrink: 0 }}>📱</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#e2e8f0' }}>Install GammaLens</div>
          <div style={{ fontSize: '11px', color: '#8e8e93', marginTop: '1px' }}>
            {isIOS ? 'Tap Share → Add to Home Screen' : 'Tap ⋮ menu → Install app'}
          </div>
        </div>
      </div>
      <button onClick={() => {
        localStorage.setItem('gl_pwa_dismissed', '1');
        setShow(false);
      }} style={{
        background: 'rgba(55,138,221,0.12)', border: '1px solid rgba(55,138,221,0.25)',
        borderRadius: '6px', padding: '5px 12px', color: '#60a5fa',
        fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        flexShrink: 0, whiteSpace: 'nowrap',
      }}>Got it</button>
    </div>
  );
}

function TrapZoneInfo({ onLearnMore }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(s => !s)}
        style={{ cursor: 'pointer', marginLeft: '5px', fontSize: '12px', color: '#636366', lineHeight: 1 }}
      >ⓘ</span>
      {show && (
        <div style={{
          position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
          marginTop: '8px', width: '320px', padding: '14px 16px',
          background: 'rgba(20,20,24,0.97)', backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: '10px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
          zIndex: 999, fontSize: '12px', color: '#cbd5e1', lineHeight: 1.65,
        }}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          <strong style={{ color: '#ff9f0a' }}>Trap Zone:</strong>{' '}
          Think of it as a price range on SPY/QQQ where dealer hedging acts like a magnet — pulling price back when it tries to move away. It shows where options market activity is most concentrated right now. This is information about market structure, not a forecast or trade signal.{' '}
          <span
            onClick={(e) => { e.stopPropagation(); if (onLearnMore) onLearnMore(); }}
            style={{ color: '#a78bfa', textDecoration: 'underline', cursor: 'pointer', textUnderlineOffset: '2px' }}
          >Learn more →</span>
        </div>
      )}
    </span>
  );
}

export default function Dashboard({ onBack, user, onLogout, onLogin, onSignup, initialTicker }) {
  const isHeatmapInit = initialTicker && initialTicker.startsWith('__HEATMAP');
  const heatmapFilter = initialTicker === '__HEATMAP_POSITIVE__' ? 'POSITIVE GAMMA'
    : initialTicker === '__HEATMAP_NEGATIVE__' ? 'NEGATIVE GAMMA'
    : initialTicker === '__HEATMAP_FLIP__' ? 'NEAR FLIP'
    : initialTicker === '__HEATMAP_FLIPPED__' ? 'FLIPPED'
    : initialTicker === '__HEATMAP_EXCEPTIONS__' ? 'EXCEPTIONS'
    : initialTicker === '__HEATMAP_SECTOR__' ? 'SECTOR'
    : 'all';
  const realTicker = initialTicker && !initialTicker.startsWith('__') ? initialTicker : 'SPY';
  const [ticker, setTicker] = useState(realTicker);
  const tickerRef = useRef(realTicker);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [navTab, setNavTab] = useState(isHeatmapInit ? 'heatmap' : 'dashboard');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [spotData, setSpotData] = useState(null);
  const [analysisTab, setAnalysisTab] = useState('gex'); // gex | pcoi | insights
  const [interpretation, setInterpretation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [heroReady, setHeroReady] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const tabContentRef = useRef(null);
  const chartRef = useRef(null);
  const panelRef = useRef(null);

  const posthog = usePostHog();
  const institutional = useInstitutionalConfirmation(ticker);

  useEffect(() => { tickerRef.current = ticker; }, [ticker]);

  useEffect(() => {
    if (!user) return;
    api.get('/api/watchlist')
      .then(r => { if (r.data?.watchlist) setWatchlist(r.data.watchlist); })
      .catch(() => {});
  }, [user]);

  const handleWatchlistToggle = async (symbol) => {
    const updated = watchlist.includes(symbol)
      ? watchlist.filter(t => t !== symbol)
      : [...watchlist, symbol].slice(0, 10);
    setWatchlist(updated);
    try {
      await api.put('/api/watchlist', { tickers: updated });
    } catch {
      setWatchlist(watchlist);
    }
  };

  const scrollToTarget = (ref) => {
    requestAnimationFrame(() => {
      if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

    const fetchGEX = async (symbol, retryCount = 0) => {
    if (loading && retryCount === 0) return;
    if (retryCount === 0) {
      posthog?.capture('ticker_selected', { ticker: symbol });
      setLoading(true);
      setHeroReady(false);
      setError(null);
      setTicker(symbol);
      setInterpretation(null);
      setSpotData(null);
    }
    try {
      const result = await getGEXAnalysis(symbol);
      // Guard: treat missing spot price as failed fetch — retry
      const spot = result?.key_levels?.spot_price ?? result?.spot_price;
      if (!spot || spot <= 0) {
        throw new Error('empty_data');
      }
      setData(result);
      setTimeout(() => setHeroReady(true), 120);
    } catch (err) {
      const maxRetries = 3;
      const isRetryable = err.message === 'empty_data' || !err.response || err.response?.status >= 500;
      if (retryCount < maxRetries && isRetryable) {
        const delay = (retryCount + 1) * 2000; // 2s, 4s, 6s
        await new Promise(r => setTimeout(r, delay));
        return fetchGEX(symbol, retryCount + 1);
      }
      const detail = err.response?.data?.detail || '';
      const isUnsupported = err.response?.status === 404 && /not supported/i.test(detail);
      if (isUnsupported) {
        setData(null);
        setInterpretation(null);
        setError(`${symbol} isn't supported yet. Pick a ticker from the dropdown.`);
      } else if (!data) {
        setError(err.response?.status === 404
          ? `No options data found for ${symbol}.`
          : `Levels unavailable — try refreshing in a moment.`);
      }
    } finally {
      if (retryCount === 0 || !loading) setLoading(false);
    }
  };

  const fetchInterpretation = async () => {
    if (!ticker) return;
    setAiLoading(true);
    try {
      const result = await getInterpretation(ticker);
      setInterpretation(result.interpretation);
    } catch {
      setInterpretation('AI insights are temporarily unavailable. Please try again later.');
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => { fetchGEX(realTicker); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 15 min during market hours + on tab focus
  useEffect(() => {
    const isMarketHours = () => {
      const now = new Date();
      const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const day = et.getDay();
      if (day < 1 || day > 5) return false;
      const holidays = [
        '2025-01-01','2025-01-20','2025-02-17','2025-04-18','2025-05-26',
        '2025-06-19','2025-07-04','2025-09-01','2025-11-27','2025-12-25',
        '2026-01-01','2026-01-19','2026-02-16','2026-04-03','2026-05-25',
        '2026-06-19','2026-07-03','2026-09-07','2026-11-26','2026-12-25',
      ];
      const dateStr = et.getFullYear() + '-' +
        String(et.getMonth() + 1).padStart(2, '0') + '-' +
        String(et.getDate()).padStart(2, '0');
      if (holidays.includes(dateStr)) return false;
      const mins = et.getHours() * 60 + et.getMinutes();
      return mins >= 570 && mins <= 990; // 9:30–4:30 ET
    };

    // Interval-based refresh every 15 min
    const interval = setInterval(() => {
      if (isMarketHours()) fetchGEX(tickerRef.current);
    }, 15 * 60 * 1000);

    // Refresh immediately when tab becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isMarketHours()) {
        fetchGEX(tickerRef.current);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [ticker]);

  // Spot price polling — every 30s during market hours when tab visible
  useEffect(() => {
    let interval = null;

    const pollSpot = async () => {
      const t = tickerRef.current;
      if (!t) return;
      try {
        const resp = await fetch(`${API_BASE}/api/spot/${t}`);
        if (resp.ok) {
          const d = await resp.json();
          if (d.ticker === tickerRef.current) {
            setSpotData(d);
          }
        }
      } catch {
        // Silently fail — spot polling is best-effort
      }
    };

    const shouldPoll = () => {
      if (document.visibilityState !== 'visible') return false;
      if (navTab !== 'dashboard') return false;
      const now = new Date();
      const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const day = et.getDay();
      if (day < 1 || day > 5) return false;
      const mins = et.getHours() * 60 + et.getMinutes();
      if (mins < 570 || mins >= 960) return false;
      return true;
    };

    const startPolling = () => {
      if (interval) clearInterval(interval);
      if (shouldPoll()) {
        pollSpot();
        interval = setInterval(() => {
          if (shouldPoll()) {
            pollSpot();
          } else {
            clearInterval(interval);
            interval = null;
          }
        }, 30000);
      }
    };

    startPolling();

    const handleSpotVisibility = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        if (interval) { clearInterval(interval); interval = null; }
      }
    };
    document.addEventListener('visibilitychange', handleSpotVisibility);

    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleSpotVisibility);
    };
  }, [ticker, navTab]);

  useEffect(() => {
    if (analysisTab === 'insights' && data && !interpretation && !aiLoading) fetchInterpretation();
  }, [analysisTab, data]);

  // When AI content finishes loading, scroll to it
  useEffect(() => {
    if (analysisTab === 'insights' && !aiLoading && interpretation) {
      setTimeout(() => scrollToTarget(tabContentRef), 80);
    }
  }, [aiLoading]);

  const levels = data?.key_levels;
  const isPos = levels?.dealer_positioning === 'POSITIVE GAMMA';
  const accent = isPos ? '#30d158' : '#ff453a';
  const accentDim = isPos ? 'rgba(48,209,88,0.07)' : 'rgba(255,69,58,0.07)';
  const isDash = navTab === 'dashboard';

  const liveSpot = spotData?.spot_price ?? levels?.spot_price;
  const liveChange = spotData?.spot_change ?? data?.spot_change;
  const liveChangePct = spotData?.spot_changepct ?? data?.spot_changepct;
  const spotCount = useCountUp(liveSpot, 1000, 2, heroReady);

  const mktStatus = (() => {
    const now = new Date();
    const etTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const d = etTime.getDay();
    if (d < 1 || d > 5) return 'closed';
    // NYSE holidays 2025–2026 (update annually)
    const holidays = [
      '2025-01-01','2025-01-20','2025-02-17','2025-04-18','2025-05-26',
      '2025-06-19','2025-07-04','2025-09-01','2025-11-27','2025-12-25',
      '2026-01-01','2026-01-19','2026-02-16','2026-04-03','2026-05-25',
      '2026-06-19','2026-07-03','2026-09-07','2026-11-26','2026-12-25',
    ];
    const dateStr = etTime.getFullYear() + '-' +
      String(etTime.getMonth() + 1).padStart(2, '0') + '-' +
      String(etTime.getDate()).padStart(2, '0');
    if (holidays.includes(dateStr)) return 'closed';
    const etMinutes = etTime.getHours() * 60 + etTime.getMinutes();
    if (etMinutes >= 570 && etMinutes < 960) return 'open';  // 9:30–16:00 ET
    if (etMinutes >= 480 && etMinutes < 570) return 'pre';   // 8:00–9:30 ET
    return 'closed';
  })();

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#f2f2f7', fontFamily: "-apple-system, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif", overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes pulse-spot { 0%,100% { box-shadow: 0 0 6px rgba(255,255,255,0.5); } 50% { box-shadow: 0 0 12px rgba(255,255,255,0.8); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes ripple { 0% { transform: scale(1); opacity: 0.5; } 100% { transform: scale(3); opacity: 0; } }
        @keyframes gl-float-up { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gl-blur-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes gl-tab-enter { from { opacity: 0; transform: translateX(10px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes mkt-ring { 0%{transform:scale(1);opacity:0.7} 100%{transform:scale(2.6);opacity:0} }
        @keyframes mkt-dot  { 0%,100%{box-shadow:0 0 0 0 rgba(48,209,88,0.6)} 50%{box-shadow:0 0 0 5px rgba(48,209,88,0)} }
        @keyframes regime-in { from{opacity:0;transform:translateY(8px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes regime-shimmer { 0%{left:-60%} 60%,100%{left:130%} }
        @keyframes regime-pulse { 0%,100%{box-shadow:0 0 0 0 var(--regime-clr,rgba(52,199,89,0.5))} 50%{box-shadow:0 0 0 6px transparent} }
        @keyframes regime-ring { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(2.4);opacity:0} }
        .gl-avatar { display: none; }
        .gl-nav { transition: color 0.15s ease, opacity 0.15s ease; }
        .gl-nav:hover { opacity: 0.7; }
        .gl-pill { transition: all 0.2s ease; }
        .gl-pill:hover { opacity: 0.8; }
        .gl-row:hover { background: rgba(255,255,255,0.02) !important; }
        .gl-mob-tabs { display: none; }
        .gl-back-dash { display: flex !important; }
        @media (max-width: 768px) {
          .gl-back-dash { display: none !important; }
          .gl-hdr { padding: 0 14px !important; }
          .gl-main { padding: 16px 14px !important; }
          .gl-lvls { grid-template-columns: repeat(2,1fr) !important; }
          .gl-dex { grid-template-columns: 1fr !important; }
          .gl-hdr-mid { display: none !important; }
          .gl-row2 { grid-template-columns: 1fr !important; }
          .gl-display-name { display: none !important; }
          .gl-beta-badge { display: none !important; }
          .gl-mkt-label { display: none !important; }
          .gl-avatar { display: flex !important; }
          .gl-mob-tabs { display: flex !important; }
          .gl-hero-inner { flex-direction: column-reverse !important; gap: 16px !important; padding: 16px !important; }
          .gl-ticker-identity { order: 99 !important; margin-bottom: 0 !important; padding-bottom: 0 !important; border-bottom: none !important; }
          .gl-hero-spot { text-align: left !important; align-items: flex-start !important; }
          .gl-hero-spot-label { justify-content: flex-start !important; }
          .gl-change-row { justify-content: flex-start !important; }
          .gl-spot-timestamp { justify-content: flex-start !important; }
          .gl-closed-badge { display: none !important; }
          .gl-closed-spot { display: inline-flex !important; }
          .gl-hero-spot-price { font-size: 36px !important; letter-spacing: -1px !important; }
          .gl-stats-row { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
          .gl-stat-item { padding-right: 0 !important; margin-right: 0 !important; border-right: none !important; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 10px 12px !important; }
          .gl-levels-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .gl-range-strip { display: none !important; }
        }
        @media (max-width: 480px) {
          .gl-lvls { grid-template-columns: 1fr 1fr !important; }
          .gl-ticker-bar { gap: 6px !important; }
          .gl-signup-btn { padding: 6px 14px !important; font-size: 12px !important; }
          .gl-logo-text { display: none !important; }
          .gl-share-btn { padding: 6px 10px !important; min-height: 32px !important; min-width: 32px !important; justify-content: center !important; }
          .gl-share-label { display: none !important; }
        }
      `}</style>

      {/* Ambient background glow */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: '-400px', left: '50%', transform: 'translateX(-50%)',
          width: '900px', height: '700px', borderRadius: '50%',
          background: data
            ? `radial-gradient(ellipse, ${accentDim} 0%, transparent 65%)`
            : 'radial-gradient(ellipse, rgba(16,185,129,0.04) 0%, transparent 65%)',
          filter: 'blur(90px)',
          transition: 'background 3s ease',
        }} />
      </div>

      {/* ═══════════════════ HEADER ═══════════════════ */}
      <header className="gl-hdr" style={{
        height: '58px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 32px', position: 'sticky', top: 0, zIndex: 300,
        background: 'rgba(13,17,23,0.95)',
        backdropFilter: 'blur(24px)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button onClick={onBack} className="gl-nav gl-home-btn" style={{
            background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
            color: '#98989f', cursor: 'pointer', fontSize: '12px',
            padding: '6px 12px', borderRadius: '8px', fontFamily: 'inherit', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '5px',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Home
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <GammaLensLogo size={26} />
            <span className="gl-logo-text" style={{ fontSize: '16px', fontWeight: 800, letterSpacing: '-0.3px', color: '#f4f4f5', fontFamily: 'Inter, -apple-system, sans-serif' }}>GammaLens</span>
            <span className="gl-beta-badge" style={{
              fontSize: '9px', fontWeight: '600', color: '#22c55e', letterSpacing: '2px',
              background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)',
              borderRadius: '10px', padding: '2px 8px',
            }}>BETA</span>
          {/* Market status — left side */}
          {mktStatus === 'open' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginLeft: '4px' }}>
              <div style={{ position: 'relative', width: '10px', height: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(48,209,88,0.35)', animation: 'mkt-ring 1.6s ease-out infinite' }} />
                <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(48,209,88,0.2)', animation: 'mkt-ring 1.6s ease-out 0.55s infinite' }} />
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#30d158', animation: 'mkt-dot 2s ease-in-out infinite', position: 'relative', zIndex: 1 }} />
              </div>
              <span className="gl-mkt-label" style={{ fontSize: '11px', color: '#30d158', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.08em' }}>Market Open</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: mktStatus === 'pre' ? '#ff9f0a' : '#ff453a' }} />
              <span className="gl-mkt-label" style={{ fontSize: '11px', color: mktStatus === 'pre' ? '#ff9f0a' : '#ff453a', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}>
                {mktStatus === 'pre' ? 'Pre-Market' : 'Market Closed'}
              </span>
            </div>
          )}
          </div>
        </div>


        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {user ? (
            <>
              {/* Desktop: text name */}
              <span className="gl-display-name" style={{ fontSize: '12px', color: '#a1a1aa', fontWeight: 500 }}>{user.display_name || user.email?.split('@')[0]}</span>
              {/* Mobile: initials avatar */}
              {(() => {
                const name = user.display_name || user.email?.split('@')[0] || '?';
                const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                return (
                  <div className="gl-avatar" title={name} style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #30d158 0%, #25a244 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 700, color: '#000',
                    flexShrink: 0, letterSpacing: '0.03em',
                  }}>{initials}</div>
                );
              })()}
              <button onClick={onLogout} className="gl-nav" style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: '6px', padding: '5px 12px', color: '#fb7185',
                fontSize: '10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
              }}>Sign out</button>
            </>
          ) : (
            <button onClick={onLogin} className="gl-nav gl-signup-btn" style={{
              background: '#30d158', border: 'none', borderRadius: '6px', padding: '7px 18px',
              color: '#000000', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              letterSpacing: '0.02em',
            }}>Sign up free</button>
          )}
        </div>
      </header>

      {/* Mobile tab strip removed — single tab was redundant */}

      {/* ═══════════════════ MAIN ═══════════════════ */}
      <main className="gl-main" style={{ maxWidth: '1360px', margin: '0 auto', padding: '24px 32px', position: 'relative', zIndex: 1 }}>
        <AnnouncementBanner />
        <PWAInstallBanner />
        <FeedbackBanner user={user} onOpen={() => setFeedbackOpen(true)} />
        <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} user={user} />

        {/* Ticker selector */}
        {isDash && (
          <div style={{ marginBottom: '24px', animation: 'gl-float-up 0.5s ease both' }}>
            <TickerSelector
              currentTicker={ticker}
              onSelect={fetchGEX}
              loading={loading}
              watchlist={watchlist}
              onWatchlistToggle={user ? handleWatchlistToggle : undefined}
              autoOpen={initialTicker === '__SEARCH__'}
            />
          </div>
        )}

        {/* ── HERO BANNER ── */}
        {isDash && data && !loading && heroReady && (
          <div style={{
            borderRadius: '14px',
            marginBottom: '16px',
            background: '#161b22',
            border: '1px solid rgba(255,255,255,0.08)',
            animation: 'gl-float-up 0.4s ease both',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* 1px gradient accent line at top */}
            <div style={{ height: '1px', background: `linear-gradient(90deg, ${accent}, ${accent}40, transparent 70%)` }} />

            <div className="gl-hero-inner" style={{ padding: '22px 28px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap' }}>

              {/* ── Ticker identity row ── */}
              <div className="gl-ticker-identity" style={{
                display: 'flex', alignItems: 'baseline', gap: '10px',
                flexBasis: '100%', marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}>
                <span style={{
                  fontSize: '22px', fontWeight: 800, color: '#ffffff',
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: '1px', lineHeight: 1,
                }}>
                  {ticker}
                </span>
                <span style={{
                  fontSize: '12px', color: '#636366',
                  fontWeight: 500, lineHeight: 1,
                }}>
                  {getTickerName(ticker)}
                </span>
              </div>

              {/* ── LEFT: regime + description + key levels ── */}
              <div style={{ flex: 1, minWidth: '220px' }}>
                {/* Regime badge row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: `${accent}12`, border: `1px solid ${accent}35`,
                    borderRadius: '8px', padding: '6px 14px',
                    position: 'relative', overflow: 'hidden',
                    animation: 'regime-in 0.5s cubic-bezier(0.16,1,0.3,1) both',
                    boxShadow: `0 0 18px ${accent}14, inset 0 1px 0 ${accent}18`,
                  }}>
                    {/* Shimmer sweep */}
                    <div style={{
                      position: 'absolute', top: 0, bottom: 0, width: '40%',
                      background: `linear-gradient(90deg, transparent, ${accent}18, transparent)`,
                      animation: 'regime-shimmer 3.5s ease-in-out infinite',
                      pointerEvents: 'none',
                    }} />
                    {/* Pulsing dot with ring */}
                    <div style={{ position: 'relative', width: '10px', height: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        background: `${accent}40`,
                        animation: 'regime-ring 2s ease-out infinite',
                      }} />
                      <div style={{
                        width: '7px', height: '7px', borderRadius: '50%',
                        background: accent,
                        boxShadow: `0 0 8px ${accent}, 0 0 14px ${accent}70`,
                        animation: `regime-pulse 2s ease-in-out infinite`,
                        ['--regime-clr']: `${accent}80`,
                        position: 'relative', zIndex: 1,
                      }} />
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 800, color: accent, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", position: 'relative' }}>
                      {isPos ? 'Positive Gamma' : 'Negative Gamma'}
                    </span>
                  </div>
                  {mktStatus === 'closed' && (() => {
                    const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
                    const day = et.getDay();
                    if (day === 0) et.setDate(et.getDate() - 2);
                    else if (day === 6) et.setDate(et.getDate() - 1);
                    else {
                      const mins = et.getHours() * 60 + et.getMinutes();
                      if (mins < 570) et.setDate(et.getDate() - (day === 1 ? 3 : 1));
                    }
                    const label = et.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <span className="gl-closed-badge" style={{ fontSize: '10px', color: '#71717a', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', padding: '2px 8px', letterSpacing: '0.04em' }}>
                        Market Closed · {label} EOD
                      </span>
                    );
                  })()}
                </div>

                {/* Regime description */}
                <p style={{ fontSize: '14px', color: '#d4d4d8', lineHeight: 1.65, margin: '0 0 20px', maxWidth: '500px', fontWeight: 400 }}>
                  {levels.market_regime}
                </p>

                {/* Contextual stats */}
                {(() => {
                  const spot = levels.spot_price;
                  const flipDist = levels.gamma_flip ? ((spot - levels.gamma_flip) / spot * 100) : null;
                  const cwDist   = levels.call_wall  ? ((levels.call_wall  - spot) / spot * 100) : null;
                  const pwDist   = levels.put_wall   ? ((spot - levels.put_wall)   / spot * 100) : null;
                  const flipNear = flipDist !== null && Math.abs(flipDist) < 1.0;
                  const atr14  = levels.atr_14  ?? null;
                  const atrPct = levels.atr_pct ?? null;
                  const stats = [
                    {
                      label: 'Flip Distance',
                      value: flipDist !== null ? `${Math.abs(flipDist).toFixed(1)}%` : '—',
                      sub: flipDist !== null ? (flipDist > 0 ? 'above flip' : 'below flip') : '',
                      color: flipNear ? '#ff9f0a' : '#a855f7',
                      urgent: flipNear,
                    },
                    {
                      label: 'Headroom',
                      value: cwDist !== null ? `+${cwDist.toFixed(1)}%` : '—',
                      sub: 'to call wall',
                      color: '#34c759',
                    },
                    {
                      label: 'Floor',
                      value: pwDist !== null ? `−${Math.abs(pwDist).toFixed(1)}%` : '—',
                      sub: 'to put wall',
                      color: '#ef4444',
                    },
                    ...(atr14 !== null ? [{
                      label: 'Daily ATR',
                      value: `$${atr14 < 10 ? atr14.toFixed(2) : atr14.toFixed(0)}`,
                      sub: atrPct !== null ? `${atrPct.toFixed(1)}% exp. move` : 'avg daily range',
                      color: '#ff9f0a',
                    }] : []),
                  ];
                  return (
                    <div className="gl-stats-row" style={{ display: 'flex', gap: '0', flexWrap: 'wrap' }}>
                      {stats.map((s, i) => (
                        <div key={i} className="gl-stat-item" style={{ paddingRight: '24px', borderRight: i < stats.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none', marginRight: i < stats.length - 1 ? '24px' : 0 }}>
                          <div style={{ fontSize: '10px', color: '#8e8e93', letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", marginBottom: '4px' }}>
                            {s.label}
                            {s.urgent && <span style={{ marginLeft: '5px', color: '#ff9f0a', animation: 'pulse 1.5s ease infinite' }}>●</span>}
                          </div>
                          <div style={{ fontSize: '17px', fontWeight: 700, color: s.color, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.3px', lineHeight: 1 }}>{s.value}</div>
                          <div style={{ fontSize: '10px', color: '#8e8e93', marginTop: '3px', fontFamily: "'JetBrains Mono', monospace" }}>{s.sub}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* ── RIGHT: spot price ── */}
              <div className="gl-hero-spot" style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div className="gl-hero-spot-label" style={{ fontSize: '10px', color: '#8e8e93', fontWeight: 600, letterSpacing: '0.12em', marginBottom: '6px', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                  <span>Spot Price</span>
                  {spotData && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      fontSize: '9px', color: '#30d158',
                      fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                      letterSpacing: '0.08em',
                    }}>
                      <span style={{
                        width: '5px', height: '5px', borderRadius: '50%',
                        background: '#30d158',
                        animation: 'pulse-dot 2s ease-in-out infinite',
                      }} />
                      LIVE
                    </span>
                  )}
                  {!spotData && mktStatus === 'closed' && (
                    <span className="gl-closed-spot" style={{
                      display: 'none', alignItems: 'center', gap: '4px',
                      fontSize: '9px', color: '#ff453a',
                      fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                      letterSpacing: '0.06em',
                    }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#ff453a' }} />
                      CLOSED
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: '52px', fontWeight: 800,
                  color: '#ffffff',
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: '-2px', lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }} className="gl-hero-spot-price">
                  ${Number(spotCount).toFixed(2)}
                </div>
                {/* Daily change row */}
                <div className="gl-change-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginTop: '6px' }}>
                  <span style={{ fontSize: '11px', color: '#8e8e93', fontFamily: "'JetBrains Mono', monospace" }}>USD</span>
                  {liveChange != null && (
                    <>
                      <span style={{
                        fontSize: '13px', fontWeight: 700,
                        fontFamily: "'JetBrains Mono', monospace",
                        color: liveChange >= 0 ? '#30d158' : '#ff453a',
                      }}>
                        {liveChange >= 0 ? '+' : ''}{liveChange.toFixed(2)}
                      </span>
                      {liveChangePct != null && (
                        <span style={{
                          fontSize: '13px', fontWeight: 700,
                          fontFamily: "'JetBrains Mono', monospace",
                          color: liveChangePct >= 0 ? '#30d158' : '#ff453a',
                        }}>
                          {liveChangePct >= 0 ? '+' : ''}{liveChangePct.toFixed(2)}%
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="gl-spot-timestamp" style={{ fontSize: '11px', color: '#aeaeb2', marginTop: '6px', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <span>{new Date(data.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })} ET · {new Date(data.timestamp).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</span>
                  {user && levels && (levels.call_wall || levels.put_wall || levels.gamma_flip) && (() => {
                    const etTime = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
                    const fmtPrice = (v) => v >= 100 ? `$${v.toFixed(0)}` : `$${v.toFixed(1)}`;
                    const fmtGex = (n) => {
                      if (n == null) return null;
                      const sign = n >= 0 ? '+' : '';
                      const abs = Math.abs(n);
                      if (abs >= 1e9) return `${sign}$${(n / 1e9).toFixed(1)}B`;
                      if (abs >= 1e6) return `${sign}$${(n / 1e6).toFixed(0)}M`;
                      return `${sign}$${n.toFixed(0)}`;
                    };
                    const lines = [`$${ticker} gamma levels as of ${etTime} ET`, ''];
                    if (levels.call_wall != null) lines.push(`Call Wall: ${fmtPrice(levels.call_wall)}`);
                    if (levels.put_wall != null) lines.push(`Put Wall: ${fmtPrice(levels.put_wall)}`);
                    if (levels.gamma_flip != null) lines.push(`Gamma Flip: ${fmtPrice(levels.gamma_flip)}`);
                    const gex = fmtGex(levels.net_gex);
                    if (gex) lines.push(`Net GEX: ${gex}`);
                    lines.push('', 'Live data: gammalens.markets', '', 'via @GammaLensApp');
                    const text = lines.join('\n');
                    const handleShare = () => {
                      posthog?.capture('share_button_clicked', {
                        ticker,
                        call_wall: levels.call_wall,
                        put_wall: levels.put_wall,
                        gamma_flip: levels.gamma_flip,
                        net_gex: levels.net_gex,
                      });
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
                    };
                    return (
                      <button onClick={handleShare} className="gl-share-btn" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        background: 'linear-gradient(135deg, rgba(29,155,240,0.15), rgba(29,155,240,0.08))',
                        border: '1px solid rgba(29,155,240,0.30)',
                        borderRadius: '8px', padding: '6px 14px', cursor: 'pointer',
                        color: '#1d9bf0', fontSize: '12px', fontWeight: 700,
                        fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.03em',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 0 8px rgba(29,155,240,0.08)',
                        minHeight: '30px',
                      }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29,155,240,0.25), rgba(29,155,240,0.14))';
                          e.currentTarget.style.borderColor = 'rgba(29,155,240,0.55)';
                          e.currentTarget.style.boxShadow = '0 0 18px rgba(29,155,240,0.30), 0 0 6px rgba(29,155,240,0.18)';
                          e.currentTarget.style.color = '#4bb4f7';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29,155,240,0.15), rgba(29,155,240,0.08))';
                          e.currentTarget.style.borderColor = 'rgba(29,155,240,0.30)';
                          e.currentTarget.style.boxShadow = '0 0 8px rgba(29,155,240,0.08)';
                          e.currentTarget.style.color = '#1d9bf0';
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        <span className="gl-share-label">Share</span>
                      </button>
                    );
                  })()}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Loading */}
        {loading && isDash && (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <div style={{
              width: '32px', height: '32px', margin: '0 auto 16px',
              border: '2px solid rgba(255,255,255,0.06)',
              borderTop: '2px solid #34c759',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <p style={{ color: '#8e8e93', fontSize: '14px' }}>Loading {ticker}</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && isDash && (() => {
          const unsupported = /isn't supported/i.test(error);
          return (
            <div style={{
              maxWidth: '380px', margin: '60px auto', textAlign: 'center', padding: '40px 28px',
              background: '#1a1a1f', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.07)',
            }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>{unsupported ? '🔍' : '📉'}</div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px', color: '#ffffff' }}>
                {unsupported ? 'Ticker not supported' : 'Data Unavailable'}
              </h3>
              <p style={{ color: '#8e8e93', fontSize: '13px', lineHeight: 1.6, margin: '0 0 20px' }}>{error}</p>
              {!unsupported && (
                <button onClick={() => fetchGEX(ticker)} style={{
                  background: '#30d158', border: 'none', borderRadius: '8px', padding: '10px 24px',
                  color: '#000000', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}>Try Again</button>
              )}
            </div>
          );
        })()}

        {/* ══════════════════════════ DATA ══════════════════════════ */}
        {data && !loading && isDash && (
          <>
            {/* TODAY EVENT ALERT — full width, above everything */}

            {/* ══════════════════════════════════════════
                ROW 1 — KEY LEVELS (4 cards, unchanged)
            ══════════════════════════════════════════ */}
            {/* ═══ KEY LEVELS — Unified Card with Range Strip ═══ */}
            {(() => {
              const spot = levels.spot_price;
              const cw = levels.call_wall;
              const pw = levels.put_wall;
              const flip = levels.gamma_flip;
              const gex = levels.net_gex;
              if (!spot || !cw || !pw) return null;

              const isPositiveGamma = gex >= 0;
              const pad = 8;
              const rangeMin = Math.min(pw, flip || pw, spot) - pad;
              const rangeMax = Math.max(cw, flip || cw, spot) + pad;
              const range = rangeMax - rangeMin;
              const toPos = (val) => ((val - rangeMin) / range * 100);

              const spotPos = toPos(spot);
              const cwPos = toPos(cw);
              const pwPos = toPos(pw);
              const flipPos = flip ? toPos(flip) : null;

              const cwDist = ((cw - spot) / spot * 100).toFixed(1);
              const pwDist = ((spot - pw) / spot * 100).toFixed(1);
              const flipDist = flip ? ((spot - flip) / spot * 100).toFixed(1) : null;

              const gexFormatted = Math.abs(gex) >= 1e9
                ? (gex / 1e9).toFixed(2) + 'B'
                : Math.abs(gex) >= 1e6
                ? (gex / 1e6).toFixed(0) + 'M'
                : formatLargeNum(gex);

              const callWallExpCount = levels.call_wall_stacking || 0;
              const putWallExpCount = levels.put_wall_stacking || 0;
              const cardData = [
                { label: 'CALL WALL', color: '#30d158', value: `$${cw.toLocaleString(undefined, {maximumFractionDigits: 0})}`, dist: `+${cwDist}%`, sub: 'headroom', barPct: Math.min(Math.abs(parseFloat(cwDist)) * 12, 100), confirmed: institutional.call_wall, stacking: callWallExpCount },
                { label: 'PUT WALL', color: '#ff453a', value: `$${pw.toLocaleString(undefined, {maximumFractionDigits: 0})}`, dist: `−${pwDist}%`, sub: 'floor', barPct: Math.min(Math.abs(parseFloat(pwDist)) * 12, 100), confirmed: institutional.put_wall, stacking: putWallExpCount },
                { label: 'GAMMA FLIP', color: '#bf5af2', value: `$${flip ? flip.toLocaleString(undefined, {maximumFractionDigits: 0}) : '—'}`, dist: flipDist ? `${parseFloat(flipDist) >= 0 ? '+' : ''}${flipDist}%` : '—', sub: flip && spot >= flip ? 'above flip' : 'below flip', barPct: flipDist ? Math.min(Math.abs(parseFloat(flipDist)) * 5, 100) : 0, confirmed: false },
                { label: 'NET GEX', color: isPositiveGamma ? '#30d158' : '#ff453a', value: gexFormatted, dist: isPositiveGamma ? 'Positive' : 'Negative', sub: isPositiveGamma ? 'Suppress moves' : 'Amplify moves', barPct: 65, confirmed: false },
              ];

              return (
                <div style={{ background: '#1a1a1f', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', padding: '16px 20px 20px', marginBottom: '12px', position: 'relative', overflow: 'hidden' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.12em', color: '#8e8e93', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>Key Levels</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <CopyLevelsButton ticker={ticker} levels={levels} strikes={data?.strikes} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: isPositiveGamma ? 'rgba(48,209,88,0.08)' : 'rgba(255,69,58,0.08)', border: `1px solid ${isPositiveGamma ? 'rgba(48,209,88,0.20)' : 'rgba(255,69,58,0.20)'}`, borderRadius: '8px', padding: '5px 10px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isPositiveGamma ? '#30d158' : '#ff453a' }} />
                        <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', color: isPositiveGamma ? '#30d158' : '#ff453a', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>{isPositiveGamma ? '+γ' : '−γ'}</span>
                        <span style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace" }}>{gexFormatted}</span>
                      </div>
                    </div>
                  </div>

                  {/* Range strip — desktop only */}
                  <div className="gl-range-strip" style={{ position: 'relative', height: '8px', background: 'rgba(255,255,255,0.04)', borderRadius: '4px', marginBottom: '24px', marginTop: '24px', overflow: 'visible' }}>
                    {flipPos !== null && <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${flipPos}%`, background: 'linear-gradient(90deg, rgba(255,69,58,0.15), rgba(255,69,58,0.03))', borderRadius: '4px 0 0 4px' }} />}
                    {flipPos !== null && <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${flipPos}%`, right: 0, background: 'linear-gradient(90deg, rgba(48,209,88,0.03), rgba(48,209,88,0.15))', borderRadius: '0 4px 4px 0' }} />}
                    {/* PW tag */}
                    <div style={{ position: 'absolute', top: '-18px', left: `${pwPos}%`, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
                      <span style={{ fontSize: '7px', fontWeight: 800, color: '#ff453a', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em', background: 'rgba(255,69,58,0.12)', border: '1px solid rgba(255,69,58,0.25)', borderRadius: '3px', padding: '1px 4px', marginBottom: '2px', whiteSpace: 'nowrap' }}>PW</span>
                      <div style={{ width: '2px', height: '18px', borderRadius: '1px', background: '#ff453a', boxShadow: '0 0 6px rgba(255,69,58,0.4)', outline: '1px solid rgba(0,0,0,0.3)' }} />
                    </div>
                    {/* GF tag */}
                    {flipPos !== null && (
                      <div style={{ position: 'absolute', top: '-18px', left: `${flipPos}%`, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
                        <span style={{ fontSize: '7px', fontWeight: 800, color: '#bf5af2', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em', background: 'rgba(191,90,242,0.12)', border: '1px solid rgba(191,90,242,0.25)', borderRadius: '3px', padding: '1px 4px', marginBottom: '2px', whiteSpace: 'nowrap' }}>GF</span>
                        <div style={{ width: '2px', height: '18px', borderRadius: '1px', background: '#bf5af2', boxShadow: '0 0 6px rgba(191,90,242,0.4)', outline: '1px solid rgba(0,0,0,0.3)' }} />
                      </div>
                    )}
                    {/* CW tag */}
                    <div style={{ position: 'absolute', top: '-18px', left: `${cwPos}%`, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
                      <span style={{ fontSize: '7px', fontWeight: 800, color: '#30d158', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em', background: 'rgba(48,209,88,0.12)', border: '1px solid rgba(48,209,88,0.25)', borderRadius: '3px', padding: '1px 4px', marginBottom: '2px', whiteSpace: 'nowrap' }}>CW</span>
                      <div style={{ width: '2px', height: '18px', borderRadius: '1px', background: '#30d158', boxShadow: '0 0 6px rgba(48,209,88,0.4)', outline: '1px solid rgba(0,0,0,0.3)' }} />
                    </div>
                    {/* Spot dot + label */}
                    <div style={{ position: 'absolute', top: '50%', left: `${spotPos}%`, transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ffffff', boxShadow: '0 0 0 2px rgba(0,0,0,0.3), 0 0 8px rgba(255,255,255,0.5)', animation: 'pulse-spot 2s ease-in-out infinite' }} />
                      <span style={{ fontSize: '7px', fontWeight: 800, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", marginTop: '4px', letterSpacing: '0.06em' }}>SPOT</span>
                    </div>
                  </div>

                  {/* 4 cards grid */}
                  <div className="gl-levels-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                    {cardData.map((card, i) => (
                      <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '16px 14px', position: 'relative', overflow: 'hidden', transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)', cursor: 'default' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'none'; }}
                      >
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', borderRadius: '10px 10px 0 0', background: `linear-gradient(90deg, ${card.color}, ${card.color}40)` }} />
                        <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', color: '#8e8e93', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: card.color, display: 'inline-block' }} />
                          {card.label}
                        </div>
                        <div style={{ fontSize: '22px', fontWeight: 800, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, marginBottom: '6px', fontVariantNumeric: 'tabular-nums' }}>{card.value}</div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: card.color, fontFamily: "'JetBrains Mono', monospace", marginBottom: '2px' }}>{card.dist}</div>
                        <div style={{ fontSize: '9px', color: '#8e8e93', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{card.sub}</div>
                        {card.confirmed && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: 600, color: '#30d158', fontFamily: "'JetBrains Mono', monospace", marginTop: '4px' }}>✓ Inst.</div>
                        )}
                        {card.stacking > 0 && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '9px', fontWeight: 600, color: card.stacking >= 3 ? '#22c55e' : card.stacking >= 2 ? '#f59e0b' : 'rgba(255,255,255,0.3)', fontFamily: "'JetBrains Mono', monospace", marginTop: card.confirmed ? '2px' : '4px' }}>
                            {card.stacking >= 3 ? '◆' : card.stacking >= 2 ? '◇' : '·'} {card.stacking} exp stacked
                          </div>
                        )}
                        <div style={{ marginTop: '10px', height: '2px', borderRadius: '1px', background: 'rgba(255,255,255,0.04)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '1px', background: card.color, width: `${card.barPct}%`, transition: 'width 1s cubic-bezier(0.16,1,0.3,1)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ══════════════════════════════════════════
                SIGN-IN GATE — everything below requires login
            ══════════════════════════════════════════ */}
            {user ? (
            <>
            {/* ══════════════════════════════════════════
                ROW 2 — MARKET STRUCTURE
                DEX always shown · Dealer + Trap for SPY/QQQ
            ══════════════════════════════════════════ */}
            <FadeReveal delay={60}>
              <div className="gl-row2" style={{ display: 'grid', gridTemplateColumns: (ticker === 'SPY' || ticker === 'QQQ') ? 'repeat(3,1fr)' : '1fr', gap: '12px', marginBottom: '12px' }}>

                {/* ── DEX Panel ── */}
                {levels.dex_resistance && (
                  <div style={{ background: '#161b22', border: '1px solid rgba(255,255,255,0.07)', borderTop: '2px solid #f97316', borderRadius: '8px', padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.5"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                      <span style={{ fontSize: '12px', color: '#f97316', fontWeight: '900', letterSpacing: '2px', fontFamily: 'monospace' }}>DELTA EXPOSURE</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#8e8e93', fontWeight: '500', marginBottom: '22px' }}>Dealers sell at Resistance, buy at Support</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {[
                        { label: 'RESISTANCE', val: `$${levels.dex_resistance?.toFixed(0)}`, color: '#f97316', bar: 72 },
                        { label: 'SUPPORT',    val: `$${levels.dex_support?.toFixed(0)}`,    color: '#06b6d4', bar: 58 },
                        { label: 'NET DEX',    val: formatLargeNum(levels.net_dex),           color: levels.net_dex >= 0 ? '#30d158' : '#ff453a', bar: 48 },
                      ].map((d, i) => (
                        <div key={i}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '7px' }}>
                            <span style={{ fontSize: '12px', color: '#aeaeb2', fontFamily: 'monospace', letterSpacing: '1px', fontWeight: '700' }}>{d.label}</span>
                            <span style={{ fontSize: '22px', fontWeight: '900', color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-1px' }}>{d.val}</span>
                          </div>
                          <AnimBar pct={d.bar} color={d.color} delay={i * 100} active={heroReady} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Dealer Flow Card (SPY/QQQ only) ── */}
                {(ticker === 'SPY' || ticker === 'QQQ') && (() => {
                  const above = levels?.dealer_flow_above ?? { level: (levels.call_wall ? Math.round((levels.call_wall + levels.spot_price) / 2) : (levels.gamma_flip ?? 0) + 4), action: 'SELL', magnitude_m: 38.4 };
                  const below = levels?.dealer_flow_below ?? { level: levels.gamma_flip ?? 0, action: 'BUY', magnitude_m: 22.1 };
                  const aboveColor = above.action === 'SELL' ? '#ff453a' : '#30d158';
                  const belowColor = below.action === 'BUY'  ? '#30d158' : '#ff453a';
                  return (
                    <div style={{ background: '#161b22', border: '1px solid rgba(255,255,255,0.07)', borderTop: '2px solid #ff9f0a', borderRadius: '8px', padding: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff9f0a" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                        <span style={{ fontSize: '12px', color: '#ff9f0a', fontWeight: '900', letterSpacing: '2px', fontFamily: 'monospace' }}>DEALER FLOW</span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#8e8e93', fontWeight: '500', marginBottom: '22px' }}>Forced hedging per $1 {ticker} move</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {[
                          { label: `ABOVE $${above.level}`, action: above.action, mag: above.magnitude_m, color: aboveColor, bar: Math.min((above.magnitude_m / 60) * 100, 100) },
                          { label: `BELOW $${below.level}`, action: below.action, mag: below.magnitude_m, color: belowColor, bar: Math.min((below.magnitude_m / 60) * 100, 100) },
                        ].map((row, i) => (
                          <div key={i}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '7px' }}>
                              <div>
                                <div style={{ fontSize: '12px', color: '#aeaeb2', fontFamily: 'monospace', letterSpacing: '1px', fontWeight: '700', marginBottom: '3px' }}>{row.label}</div>
                                <div style={{ fontSize: '12px', color: row.color, fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ fontSize: '10px' }}>{row.action === 'SELL' ? '▼' : '▲'}</span> {row.action}
                                </div>
                              </div>
                              <span style={{ fontSize: '22px', fontWeight: '900', color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-1px' }}>${row.mag}M</span>
                            </div>
                            <AnimBar pct={row.bar} color={row.color} delay={i * 150} active={heroReady} />
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: '16px', fontSize: '11px', color: '#8e8e93', fontStyle: 'italic', lineHeight: 1.5 }}>Mechanical — happens automatically as price moves</div>
                    </div>
                  );
                })()}

                {/* ── Trap Zone Card (SPY/QQQ only) ── */}
                {(ticker === 'SPY' || ticker === 'QQQ') && (() => {
                  const trap = levels?.trap_zone ?? { active: false, low: levels.gamma_flip ?? 0, high: (levels.gamma_flip ?? 0) + 4, candles: 0, compression_pct: 0, bias: 'neutral' };
                  const spot = levels.spot_price;
                  const tPct = Math.min(Math.max(((spot - trap.low) / (Math.max(trap.high - trap.low, 1))) * 100, 2), 98);
                  const cardColor = trap.active ? '#ff9f0a' : '#aeaeb2';
                  const biasColor = trap.bias === 'bearish' ? '#ff453a' : trap.bias === 'bullish' ? '#30d158' : '#636366';
                  return (
                    <div style={{ background: '#161b22', border: '1px solid rgba(255,255,255,0.07)', borderTop: `2px solid ${cardColor}`, borderRadius: '8px', padding: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={cardColor} strokeWidth="2.5"><polyline points="8 6 2 12 8 18"/><polyline points="16 6 22 12 16 18"/></svg>
                          <span style={{ fontSize: '12px', color: cardColor, fontWeight: '900', letterSpacing: '2px', fontFamily: 'monospace' }}>TRAP ZONE</span>
                          <TrapZoneInfo onLearnMore={() => { setNavTab('learn'); }} />
                        </div>
                        {trap.active && (
                          <span style={{ fontSize: '11px', fontWeight: '900', color: '#ff9f0a', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '4px', padding: '3px 8px', letterSpacing: '1px', fontFamily: 'monospace', animation: 'pulse 2s infinite' }}>● ACTIVE</span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#8e8e93', fontWeight: '500', marginBottom: '22px' }}>High-gamma compression band</div>

                      {/* Range track */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '13px', color: '#ff453a', fontFamily: "'JetBrains Mono', monospace", fontWeight: '700' }}>${Number(trap.low).toFixed(0)}</span>
                          <span style={{ fontSize: '13px', color: '#aeaeb2', fontFamily: 'monospace' }}>${spot.toFixed(2)} spot</span>
                          <span style={{ fontSize: '13px', color: '#30d158', fontFamily: "'JetBrains Mono', monospace", fontWeight: '700' }}>${Number(trap.high).toFixed(0)}</span>
                        </div>
                        <div style={{ position: 'relative', height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', overflow: 'visible' }}>
                          <div style={{ position: 'absolute', inset: 0, borderRadius: '4px', background: 'linear-gradient(90deg, rgba(239,68,68,0.25), rgba(251,191,36,0.08), rgba(16,185,129,0.25))' }} />
                          <div style={{ position: 'absolute', top: '50%', left: `${tPct}%`, width: '14px', height: '14px', borderRadius: '50%', background: '#fff', border: '2px solid #000000', boxShadow: `0 0 0 3px ${cardColor}60`, transform: 'translate(-50%,-50%)', transition: 'left .8s cubic-bezier(.34,1.56,.64,1)' }} />
                        </div>
                      </div>

                      {/* Stats — only show when active or values are meaningful */}
                      {(trap.candles > 0 || trap.compression_pct > 0) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                          {trap.candles > 0 && (
                            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px 14px' }}>
                              <div style={{ fontSize: '12px', color: '#aeaeb2', fontFamily: 'monospace', letterSpacing: '1px', marginBottom: '5px', fontWeight: '700' }}>CANDLES TRAPPED</div>
                              <div style={{ fontSize: '28px', fontWeight: '900', color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-1px' }}>{trap.candles}</div>
                            </div>
                          )}
                          {trap.compression_pct > 0 && (
                            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px 14px' }}>
                              <div style={{ fontSize: '12px', color: '#aeaeb2', fontFamily: 'monospace', letterSpacing: '1px', marginBottom: '5px', fontWeight: '700' }}>COMPRESSION</div>
                              <div style={{ fontSize: '28px', fontWeight: '900', color: cardColor, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-1px' }}>{trap.compression_pct}%</div>
                            </div>
                          )}
                        </div>
                      )}
                      {trap.compression_pct > 0 && (
                        <AnimBar pct={trap.compression_pct} color={cardColor} delay={200} active={heroReady} />
                      )}

                      {trap.active ? (
                        <div style={{ marginTop: '14px', padding: '10px 14px', background: `${biasColor}08`, border: `1px solid ${biasColor}18`, borderRadius: '8px', fontSize: '13px', color: '#8898aa', lineHeight: 1.6 }}>
                          <span style={{ color: '#ebebf5', fontWeight: '700' }}>Watch ${trap.bias === 'bearish' ? trap.low : trap.high}.</span>{' '}
                          {trap.bias === 'bearish' ? 'Close below → sharp flush.' : 'Close above → sharp squeeze.'}
                        </div>
                      ) : (() => {
                        // Distinguish: genuinely no compression vs high compression but wide band
                        const pct = trap.compression_pct || 0;
                        const bw = trap.high && trap.low && levels.spot_price
                          ? ((trap.high - trap.low) / levels.spot_price * 100).toFixed(1)
                          : null;
                        const msg = pct >= 55
                          ? `${pct}% of gamma concentrated between $${trap.low}–$${trap.high} but band is wide (${bw}%). Watch for tightening toward a squeeze.`
                          : pct >= 30
                          ? `Moderate gamma buildup (${pct}%) — no compression yet. Dealers not pinned.`
                          : 'No compression detected — price moving freely between gamma levels.';
                        const msgColor = pct >= 55 ? '#ff9f0a' : '#aeaeb2';
                        return (
                          <div style={{ marginTop: '14px', padding: '10px 14px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${pct >= 55 ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.04)'}`, borderRadius: '8px', fontSize: '12px', color: msgColor, lineHeight: 1.6, fontFamily: 'monospace' }}>
                            {msg}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            </FadeReveal>

            {/* ══════════════════════════════════════════
                ADMIN DEBUG BAR — trap zone monitoring
                Only visible to admin. Invisible to users.
            ══════════════════════════════════════════ */}
            {user?.email === 'hinandhahere@gmail.com' && (ticker === 'SPY' || ticker === 'QQQ') && (() => {
              const trap = levels?.trap_zone;
              if (!trap) return null;
              const bandWidth = trap.high && trap.low && levels.spot_price
                ? ((trap.high - trap.low) / levels.spot_price * 100).toFixed(2)
                : 'N/A';
              const statusColor = trap.active ? '#30d158' : '#ff9f0a';
              return (
                <div style={{
                  margin: '8px 0', padding: '10px 16px', borderRadius: '12px',
                  background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(99,102,241,0.3)',
                  fontSize: '11px', fontFamily: 'monospace', color: '#aeaeb2',
                  display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                }}>
                  <span style={{ color: '#6366f1', fontWeight: '900', letterSpacing: '1px' }}>🔧 TRAP DEBUG — {ticker}</span>
                  <span>active=<span style={{ color: statusColor, fontWeight: '700' }}>{String(trap.active)}</span></span>
                  <span>compression=<span style={{ color: '#ff9f0a', fontWeight: '700' }}>{trap.compression_pct}%</span></span>
                  <span>bias=<span style={{ color: trap.bias === 'bearish' ? '#ff453a' : trap.bias === 'bullish' ? '#30d158' : '#8e8e93', fontWeight: '700' }}>{trap.bias}</span></span>
                  <span>low=<span style={{ color: '#ffffff' }}>${trap.low}</span></span>
                  <span>high=<span style={{ color: '#ffffff' }}>${trap.high}</span></span>
                  <span>band=<span style={{ color: '#ffffff' }}>{bandWidth}%</span></span>
                  <span style={{ color: '#8e8e93' }}>threshold: compression≥55% + band&lt;0.8%</span>
                </div>
              );
            })()}

            {false && false && (() => {
              const trap = levels?.trap_zone ?? { active: false, low: levels.gamma_flip ?? 0, high: (levels.gamma_flip ?? 0) + 4, candles: 0, compression_pct: 0, bias: 'neutral' };
              const above = levels?.dealer_flow_above ?? { level: (levels.call_wall ? Math.round((levels.call_wall + levels.spot_price) / 2) : (levels.gamma_flip ?? 0) + 4), action: 'SELL', magnitude_m: 38.4 };
              const below = levels?.dealer_flow_below ?? { level: levels.gamma_flip ?? 0, action: 'BUY', magnitude_m: 22.1 };

              if (!trap.active) return null;

              // Determine trigger direction based on bias + spot proximity
              const spot = levels.spot_price;
              const distToLow = Math.abs(spot - trap.low);
              const distToHigh = Math.abs(spot - trap.high);
              const bearish = trap.bias === 'bearish';
              const triggerLevel = bearish ? trap.low : trap.high;
              const triggerDir = bearish ? '↓' : '↑';
              const triggerColor = bearish ? '#ff453a' : '#30d158';

              // Dealer reaction on breakout
              const reaction = bearish ? below : above;
              const reactionColor = bearish ? '#30d158' : '#ff453a'; // counter-trend reaction
              const reactionVerb = reaction.action === 'BUY' ? 'forced buying' : 'forced selling';

              // Squeeze probability — based on compression % + candles trapped
              // Higher compression + more candles = higher probability of imminent break
              const compressionScore = trap.compression_pct / 100; // 0-1
              const candleScore = Math.min(trap.candles / 20, 1);   // caps at 20 candles
              const proximityScore = 1 - (Math.min(distToLow, distToHigh) / ((trap.high - trap.low) / 2)); // closer to edge = higher
              const rawProb = (compressionScore * 0.5 + candleScore * 0.3 + proximityScore * 0.2) * 100;
              const probability = Math.min(Math.max(Math.round(rawProb), 35), 92); // clamp 35-92%

              // Probability tier
              const probColor = probability >= 75 ? '#ff453a' : probability >= 55 ? '#ff9f0a' : '#8e8e93';
              const probLabel = probability >= 75 ? 'HIGH' : probability >= 55 ? 'ELEVATED' : 'MODERATE';

              // Narrative — concise, institutional
              const narrative = bearish
                ? `Price coiled ${trap.candles} candles at ${trap.compression_pct}% compression. Break below $${trap.low} triggers $${reaction.magnitude_m}M dealer ${reactionVerb} — expect initial momentum, then fade.`
                : `Price coiled ${trap.candles} candles at ${trap.compression_pct}% compression. Break above $${trap.high} triggers $${reaction.magnitude_m}M dealer ${reactionVerb} — expect initial momentum, then fade.`;

              return (
                <FadeReveal delay={70}>
                  <div style={{
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: '12px',
                    background: 'linear-gradient(135deg, rgba(251,191,36,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                    border: '1px solid rgba(251,191,36,0.2)',
                    boxShadow: '0 0 40px rgba(251,191,36,0.04)',
                    animation: 'gl-float-up 0.5s ease both',
                  }}>
                    {/* Animated top border */}
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                      background: `linear-gradient(90deg, transparent, #ff9f0a, ${triggerColor}, transparent)`,
                      animation: 'gl-border-glow 3s ease infinite',
                    }} />

                    <div style={{ padding: '20px 24px' }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff9f0a" strokeWidth="2.5">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                          </svg>
                          <span style={{ fontSize: '12px', color: '#ff9f0a', fontWeight: '900', letterSpacing: '2.5px', fontFamily: 'monospace' }}>
                            SQUEEZE SETUP — {ticker}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '11px', fontWeight: '900', color: probColor,
                            background: `${probColor}12`, border: `1px solid ${probColor}30`,
                            borderRadius: '6px', padding: '3px 10px', letterSpacing: '1.5px', fontFamily: 'monospace',
                          }}>{probLabel} PROBABILITY</span>
                          <span style={{
                            fontSize: '11px', fontWeight: '900', color: '#ff9f0a',
                            background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)',
                            borderRadius: '6px', padding: '3px 10px', letterSpacing: '1px', fontFamily: 'monospace',
                            animation: 'pulse 2s infinite',
                          }}>● ACTIVE</span>
                        </div>
                      </div>

                      {/* 4 metric columns */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>

                        {/* Trigger Level */}
                        <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '14px', padding: '14px 16px', border: `1px solid ${triggerColor}22` }}>
                          <div style={{ fontSize: '11px', color: '#8e8e93', fontFamily: 'monospace', letterSpacing: '1px', fontWeight: '700', marginBottom: '6px' }}>TRIGGER</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                            <span style={{ fontSize: '24px', fontWeight: '900', color: triggerColor, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-1px' }}>
                              ${triggerLevel}
                            </span>
                            <span style={{ fontSize: '18px', color: triggerColor, fontWeight: '900' }}>{triggerDir}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#8e8e93', marginTop: '4px', fontFamily: 'monospace' }}>
                            {bearish ? 'break below' : 'break above'}
                          </div>
                        </div>

                        {/* Bias */}
                        <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '14px', padding: '14px 16px', border: `1px solid ${triggerColor}22` }}>
                          <div style={{ fontSize: '11px', color: '#8e8e93', fontFamily: 'monospace', letterSpacing: '1px', fontWeight: '700', marginBottom: '6px' }}>BIAS</div>
                          <div style={{ fontSize: '20px', fontWeight: '900', color: triggerColor, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px', textTransform: 'uppercase' }}>
                            {trap.bias}
                          </div>
                          <div style={{ fontSize: '11px', color: '#8e8e93', marginTop: '4px', fontFamily: 'monospace' }}>
                            {trap.candles} candles trapped
                          </div>
                        </div>

                        {/* Dealer Reaction */}
                        <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '14px', padding: '14px 16px', border: `1px solid ${reactionColor}22` }}>
                          <div style={{ fontSize: '11px', color: '#8e8e93', fontFamily: 'monospace', letterSpacing: '1px', fontWeight: '700', marginBottom: '6px' }}>DEALER REACTION</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                            <span style={{ fontSize: '24px', fontWeight: '900', color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-1px' }}>
                              ${reaction.magnitude_m}M
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: reactionColor, marginTop: '4px', fontFamily: 'monospace', fontWeight: '700' }}>
                            {reaction.action === 'BUY' ? '▲ FORCED BUY' : '▼ FORCED SELL'}
                          </div>
                        </div>

                        {/* Probability */}
                        <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '14px', padding: '14px 16px', border: `1px solid ${probColor}22` }}>
                          <div style={{ fontSize: '11px', color: '#8e8e93', fontFamily: 'monospace', letterSpacing: '1px', fontWeight: '700', marginBottom: '6px' }}>SQUEEZE PROB</div>
                          <div style={{ fontSize: '28px', fontWeight: '900', color: probColor, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-1.5px' }}>
                            {probability}%
                          </div>
                          <div style={{ marginTop: '6px', height: '3px', borderRadius: '2px', background: 'rgba(255,255,255,0.04)' }}>
                            <div style={{ height: '100%', borderRadius: '2px', background: probColor, width: `${probability}%`, transition: 'width 1s ease' }} />
                          </div>
                        </div>
                      </div>

                      {/* Compression progress bar */}
                      <div style={{ marginBottom: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#8e8e93', fontFamily: 'monospace', fontWeight: '700', letterSpacing: '1px' }}>COMPRESSION</span>
                          <span style={{ fontSize: '11px', color: '#aeaeb2', fontFamily: 'monospace' }}>
                            ${Number(trap.low).toFixed(0)} ←→ ${Number(trap.high).toFixed(0)} · spot ${spot?.toFixed(2)}
                          </span>
                          <span style={{ fontSize: '11px', color: '#ff9f0a', fontFamily: 'monospace', fontWeight: '700' }}>{trap.compression_pct}%</span>
                        </div>
                        <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.03)', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: '3px',
                            background: `linear-gradient(90deg, rgba(251,191,36,0.3), #ff9f0a)`,
                            width: `${trap.compression_pct}%`,
                            transition: 'width 1.2s cubic-bezier(0.34,1.56,0.64,1)',
                            boxShadow: '0 0 8px rgba(251,191,36,0.4)',
                          }} />
                        </div>
                      </div>

                      {/* Narrative */}
                      <div style={{
                        padding: '12px 16px',
                        background: 'rgba(0,0,0,0.15)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.03)',
                        fontSize: '13px', color: '#aeaeb2', lineHeight: 1.65,
                      }}>
                        <span style={{ color: '#ff9f0a', fontWeight: '700' }}>Watch ${triggerLevel}. </span>
                        {narrative}
                        <span style={{ color: '#8e8e93', fontSize: '12px' }}> — Mechanical, not directional.</span>
                      </div>
                    </div>
                  </div>
                </FadeReveal>
              );
            })()}

            {/* ══════════════════════════════════════════
                ROW 3 — ANALYSIS PANEL + OPTIONAL CHART
                GEX/Structure/AI full-width by default.
                Live Chart toggle slides in below.
            ══════════════════════════════════════════ */}
            <FadeReveal delay={80}>
              <div ref={panelRef} style={{
                scrollMarginTop: '76px',
                background: '#161b22',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '10px',
              }}>
                {/* ── HORIZONTAL TAB BAR ── */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0 20px',
                }}>
                  <div style={{ display: 'flex' }}>
                    {[
                      { id: 'gex',      label: 'GEX Analysis',    color: '#0a84ff' },
                      { id: 'pcoi',     label: 'Market Structure', color: '#ff9f0a', hide: ticker !== 'SPY' && ticker !== 'QQQ' },
                      { id: 'insights', label: 'AI Insights',      color: '#30d158' },
                    ].filter(t => !t.hide).map(tab => {
                      const on = analysisTab === tab.id;
                      return (
                        <button key={tab.id} onClick={() => {
                          setAnalysisTab(tab.id);
                          if (tab.id === 'insights' && !interpretation) fetchInterpretation();
                          scrollToTarget(panelRef);
                        }} style={{
                          padding: '14px 16px',
                          background: 'transparent', border: 'none',
                          borderBottom: on ? `2px solid ${tab.color}` : '2px solid transparent',
                          marginBottom: '-1px',
                          color: on ? '#ffffff' : '#636366',
                          fontSize: '13px', fontWeight: on ? '600' : '500',
                          cursor: 'pointer', fontFamily: 'inherit',
                          transition: 'color 0.15s', outline: 'none',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { if (!on) e.currentTarget.style.color = '#aeaeb2'; }}
                        onMouseLeave={e => { if (!on) e.currentTarget.style.color = '#636366'; }}
                        >
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => {
                    const next = !showChart;
                    setShowChart(next);
                    if (next) setTimeout(() => scrollToTarget(chartRef), 60);
                  }} style={{
                    display: 'flex', alignItems: 'center', gap: '7px',
                    padding: '6px 12px', borderRadius: '6px', border: 'none',
                    background: showChart ? 'rgba(99,102,241,0.12)' : 'transparent',
                    color: showChart ? '#818cf8' : '#636366',
                    fontSize: '12px', fontWeight: '500',
                    cursor: 'pointer', fontFamily: 'inherit', outline: 'none',
                    transition: 'all 0.15s',
                  }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                    Live Chart
                    <div style={{ width: '22px', height: '12px', borderRadius: '6px', background: showChart ? '#6366f1' : 'rgba(255,255,255,0.08)', position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: '2px', left: showChart ? '11px' : '2px', width: '8px', height: '8px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                    </div>
                  </button>
                </div>

                {/* ── TAB CONTENT ── */}
                <div>

                  {/* GEX tab */}
                  {analysisTab === 'gex' && (
                    <div ref={tabContentRef} style={{ padding: '24px', scrollMarginTop: '76px', animation: 'gl-tab-enter 0.2s ease both' }}>
                      <SectionHeader label="GEX BY STRIKE" ticker={ticker} gradient="linear-gradient(180deg, #0a84ff, #6366f1)" />
                      <GEXChart strikes={data.strikes} keyLevels={levels} />
                      <div style={{ marginTop: '20px' }}>
                        <button onClick={() => {
                          const t = document.getElementById('gl-tbl');
                          const a = document.getElementById('gl-arr');
                          if (t) t.style.display = t.style.display === 'none' ? 'block' : 'none';
                          if (a) a.textContent = t?.style.display === 'none' ? '▼' : '▲';
                        }} style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          background: '#1a1a1f', border: '1px solid rgba(255,255,255,0.07)',
                          borderRadius: '8px', padding: '12px 16px', color: '#98989f',
                          fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                            STRIKE TABLE · {data.strikes.filter(s => s.call_gex !== 0 || s.put_gex !== 0 || s.net_gex !== 0).length} STRIKES
                          </span>
                          <span id="gl-arr">▼</span>
                        </button>
                        <div id="gl-tbl" style={{ display: 'none', marginTop: '8px', overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                                {['Strike', 'Call GEX', 'Put GEX', 'Net GEX'].map((h, i) => (
                                  <th key={i} style={{ textAlign: 'right', padding: '10px 14px', fontWeight: '800', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'monospace', color: i === 1 ? '#30d158' : i === 2 ? '#ff453a' : '#636366' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {data.strikes.filter(s => s.call_gex !== 0 || s.put_gex !== 0 || s.net_gex !== 0).map((s, i) => {
                                const isCW = s.strike === levels?.call_wall;
                                const isPW = s.strike === levels?.put_wall;
                                const isF  = s.strike === levels?.gamma_flip;
                                return (
                                  <tr key={i} className="gl-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.025)', background: isCW ? 'rgba(16,185,129,0.04)' : isPW ? 'rgba(239,68,68,0.04)' : isF ? 'rgba(167,139,250,0.04)' : 'transparent', transition: 'background 0.15s' }}>
                                    <td style={{ textAlign: 'right', padding: '9px 14px', color: '#8e8e93', fontFamily: 'monospace', fontSize: '13px' }}>
                                      ${s.strike}
                                      {isCW && <MTag c="#30d158">CW</MTag>}
                                      {isPW && <MTag c="#ff453a">PW</MTag>}
                                      {isF  && <MTag c="#bf5af2">FLIP</MTag>}
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '9px 14px', color: '#30d158', fontFamily: 'monospace', fontSize: '13px' }}>{fmt(s.call_gex)}</td>
                                    <td style={{ textAlign: 'right', padding: '9px 14px', color: '#ff453a', fontFamily: 'monospace', fontSize: '13px' }}>{fmt(s.put_gex)}</td>
                                    <td style={{ textAlign: 'right', padding: '9px 14px', fontWeight: '700', fontFamily: 'monospace', fontSize: '13px', color: s.net_gex >= 0 ? '#30d158' : '#ff453a' }}>{fmt(s.net_gex)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Structure tab */}
                  {analysisTab === 'pcoi' && (ticker === 'SPY' || ticker === 'QQQ') && (
                    <div ref={tabContentRef} style={{ padding: '24px', scrollMarginTop: '76px', animation: 'gl-tab-enter 0.2s ease both' }}>
                      <SectionHeader label="MARKET STRUCTURE" ticker={ticker} badge={<span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>Put/Call Open Interest</span>} gradient="linear-gradient(180deg, #ff9f0a, #ff453a)" />
                      <PCOIChart ticker={ticker} />
                    </div>
                  )}


                  {/* AI Insights tab */}
                  {analysisTab === 'insights' && (
                    <div ref={tabContentRef} style={{ padding: '24px', scrollMarginTop: '76px', animation: 'gl-tab-enter 0.2s ease both' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(59,130,246,0.08))', border: '1px solid rgba(16,185,129,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <GammaLensLogo size={20} />
                          </div>
                          <div>
                            <div style={{ fontSize: '15px', fontWeight: '800', color: '#fff' }}>AI Market Insights</div>
                            <div style={{ fontSize: '11px', color: '#aeaeb2', fontFamily: 'monospace', marginTop: '1px' }}>{ticker} · {new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })} ET</div>
                          </div>
                        </div>
                        {aiLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#30d158' }}>
                            <div style={{ width: '14px', height: '14px', border: '2px solid rgba(52,199,89,0.12)', borderTop: '2px solid #34c759', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                            Thinking…
                          </div>
                        ) : (
                          <button onClick={fetchInterpretation} style={{ background: '#1a1a1f', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '7px 14px', color: '#98989f', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>↺ Refresh</button>
                        )}
                      </div>
                      <div style={{ background: '#0f0f12', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '24px' }}>
                        {aiLoading ? (
                          <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                            <div style={{ width: '42px', height: '42px', border: '2px solid rgba(52,199,89,0.08)', borderTop: '2px solid #34c759', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
                            <p style={{ color: '#aeaeb2', fontSize: '13px' }}>Analyzing {ticker} positioning…</p>
                          </div>
                        ) : interpretation ? (
                          <>
                            <div style={{ color: '#d4d4d8', fontSize: '15px', lineHeight: 1.9, maxHeight: !user ? '180px' : 'none', overflow: !user ? 'hidden' : 'visible', position: 'relative' }}>
                              {interpretation.split('\n').map((line, i) => {
                                const t = line.trim().replace(/\*\*/g, '');
                                if (!t) return <div key={i} style={{ height: '10px' }} />;
                                const HDRS = ["Current Setup", "Today's Read", "The Range", "What Works Here", "The Line in the Sand", "The Bottom Line"];
                                const HC = { "Current Setup": '#4da6ff', "Today's Read": '#4da6ff', "The Range": '#30d158', "What Works Here": '#bf5af2', "The Line in the Sand": '#ff9f0a', "The Bottom Line": '#38bdf8' };
                                const m = HDRS.find(h => t === h || t.startsWith(h + ':') || t.startsWith(h + ' —'));
                                if (m) return <div key={i} style={{ fontSize: '12px', fontWeight: '900', color: HC[m] || '#4da6ff', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'monospace', marginTop: i > 0 ? '20px' : 0, marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${HC[m] || '#4da6ff'}18` }}>{m}</div>;
                                return <p key={i} style={{ margin: '0 0 10px' }}>{t}</p>;
                              })}
                            </div>
                            {!user && (
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.95) 50%)', padding: '70px 24px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <p style={{ color: '#ffffff', fontSize: '15px', fontWeight: 900, margin: '0 0 6px' }}>Full analysis — free account</p>
                                <p style={{ color: '#aeaeb2', fontSize: '12px', margin: '0 0 16px' }}>All 100 tickers, no card needed</p>
                                <button onClick={onSignup || onLogin} style={{ background: 'linear-gradient(135deg, #30d158, #25a244)', border: 'none', borderRadius: '12px', padding: '11px 32px', color: '#fff', fontSize: '14px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 24px rgba(16,185,129,0.3)' }}>Sign Up Free →</button>
                              </div>
                            )}
                          </>
                        ) : (
                          <p style={{ color: '#8e8e93', fontSize: '13px' }}>Hit Refresh to generate AI interpretation.</p>
                        )}
                      </div>
                      {interpretation && !aiLoading && (
                        <div style={{ marginTop: '12px', padding: '10px 14px', background: 'rgba(234,179,8,0.03)', border: '1px solid rgba(234,179,8,0.07)', borderRadius: '8px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                          <span style={{ fontSize: '12px', color: '#aeaeb2', lineHeight: 1.5 }}>Educational only — not financial advice. GEX from public options data. DYOR before trading.</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Live Chart pane */}
                  {showChart && (
                    <div ref={chartRef} style={{ borderTop: '1px solid rgba(255,255,255,0.07)', scrollMarginTop: '76px', animation: 'gl-tab-enter 0.2s ease both' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#aeaeb2', letterSpacing: '1.5px', fontFamily: 'monospace' }}>{ticker} · LIVE CHART</span>
                        </div>
                        <span style={{ fontSize: '12px', color: '#8e8e93' }}>Powered by TradingView</span>
                      </div>
                      <div>
                        <PriceChart ticker={ticker} keyLevels={levels} />
                      </div>
                    </div>
                  )}

                </div>
              </div>

            </FadeReveal>

            {/* Footer */}
            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
              <span style={{ fontSize: '12px', color: '#8e8e93', fontFamily: "'JetBrains Mono', monospace" }}>{new Date(data.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/New_York' })} ET · {mktStatus === 'open' ? 'Live options chain' : 'EOD options chain'}</span>
              <span style={{ fontSize: '11px', color: '#30d158', fontWeight: 700, letterSpacing: '0.5px' }}>Free Beta</span>
            </div>
            </>) : (
              /* ── Sign-in gate ── */
              <div style={{ margin: '12px 0 40px', position: 'relative', display: 'flex', justifyContent: 'center', paddingTop: '16px', paddingBottom: '16px' }}>
                <style>{`
                  @keyframes royal-shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
                  @keyframes royal-glow { 0%,100%{box-shadow:0 0 32px rgba(52,199,89,0.12),0 0 80px rgba(52,199,89,0.04),inset 0 1px 0 rgba(255,255,255,0.1)} 50%{box-shadow:0 0 48px rgba(52,199,89,0.22),0 0 120px rgba(52,199,89,0.08),inset 0 1px 0 rgba(255,255,255,0.14)} }
                  @keyframes royal-dot { 0%,100%{box-shadow:0 0 0 0 rgba(52,199,89,0.6)} 50%{box-shadow:0 0 0 5px rgba(52,199,89,0)} }
                  @keyframes royal-in { from{opacity:0;transform:translateY(20px) scale(0.96)} to{opacity:1;transform:translateY(0) scale(1)} }
                  .royal-btn:hover { background: linear-gradient(135deg,#fff 0%,#e8ffe8 100%) !important; box-shadow: 0 0 0 3px rgba(52,199,89,0.25), 0 8px 32px rgba(52,199,89,0.2) !important; }
                `}</style>

                {/* Blurred preview rows — decorative backdrop, absolutely positioned */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  filter: 'blur(5px)', opacity: 0.18, pointerEvents: 'none', userSelect: 'none',
                  padding: '24px', background: '#161b22',
                  border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px',
                  display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px',
                  overflow: 'hidden',
                }}>
                  {[
                    { label: 'Delta Exposure', val: '$638', sub: 'Resistance' },
                    { label: 'Dealer Positioning', val: '+2.4B', sub: 'Net long gamma' },
                    { label: 'GEX Skew', val: '67%', sub: 'Call dominance' },
                    { label: 'Flip Distance', val: '1.2%', sub: 'above flip' },
                    { label: 'Headroom', val: '+1.0%', sub: 'to call wall' },
                    { label: 'Daily ATR', val: '$4.80', sub: '0.7% exp. move' },
                  ].map((s, i) => (
                    <div key={i} style={{ background: '#0f0f12', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '16px 18px' }}>
                      <div style={{ fontSize: '10px', color: '#71717a', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: "'JetBrains Mono',monospace", marginBottom: '8px' }}>{s.label}</div>
                      <div style={{ fontSize: '22px', fontWeight: 800, color: '#f2f2f7', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-1px' }}>{s.val}</div>
                      <div style={{ fontSize: '11px', color: '#3f3f46', marginTop: '4px', fontFamily: "'JetBrains Mono',monospace" }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Dark fade over the backdrop */}
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '16px', pointerEvents: 'none',
                  background: 'linear-gradient(to bottom, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.82) 100%)',
                }} />

                {/* Glass card — normal flow, centered */}
                <div style={{
                  position: 'relative', zIndex: 2,
                  textAlign: 'center', padding: '40px 36px 36px',
                  background: 'rgba(14,14,18,0.65)',
                  backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '24px',
                  animation: 'royal-in 0.6s cubic-bezier(0.16,1,0.3,1) both, royal-glow 4s ease-in-out infinite',
                  overflow: 'hidden',
                  maxWidth: '380px', width: '100%',
                }}>
                  {/* Top accent line shimmer */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: '1px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(52,199,89,0.8) 30%, rgba(180,255,180,0.9) 50%, rgba(52,199,89,0.8) 70%, transparent 100%)',
                    backgroundSize: '200% auto',
                    animation: 'royal-shimmer 3s linear infinite',
                  }} />

                  {/* Ambient glow orb */}
                  <div style={{
                    position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)',
                    width: '280px', height: '220px', borderRadius: '50%',
                    background: 'radial-gradient(ellipse, rgba(52,199,89,0.1) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  }} />

                  {/* Free pill */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '5px 16px', borderRadius: '100px', marginBottom: '22px',
                    background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.25)',
                    boxShadow: '0 0 14px rgba(52,199,89,0.1)', position: 'relative',
                  }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#34c759', boxShadow: '0 0 6px #34c759', animation: 'royal-dot 2s ease-in-out infinite' }} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#34c759', letterSpacing: '0.06em', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>Free · No credit card</span>
                  </div>

                  {/* Headline with shimmer */}
                  <div style={{
                    fontSize: '26px', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.15, marginBottom: '12px',
                    background: 'linear-gradient(135deg, #f2f2f7 0%, #ffffff 40%, #b8f5c8 70%, #f2f2f7 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    animation: 'royal-shimmer 5s linear infinite',
                    fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif",
                  }}>
                    See the full picture.
                  </div>

                  <p style={{ fontSize: '13px', color: '#8a8a95', lineHeight: 1.7, margin: '0 0 28px', maxWidth: '280px', marginLeft: 'auto', marginRight: 'auto' }}>
                    Delta exposure, market structure, GEX charts and AI insights — unlocked with a free account.
                  </p>

                  <button className="royal-btn" onClick={onSignup || onLogin} style={{
                    padding: '13px 40px',
                    background: 'linear-gradient(135deg, #f2f2f7 0%, #e6ffe6 100%)',
                    border: 'none', borderRadius: '14px',
                    color: '#0a0a0a', fontSize: '14px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                    letterSpacing: '-0.02em',
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.12), 0 4px 24px rgba(52,199,89,0.15)',
                    transition: 'background 0.2s, box-shadow 0.2s', width: '100%',
                  }}>
                    Create free account →
                  </button>

                  <div style={{ marginTop: '16px', fontSize: '12px', color: '#3f3f46' }}>
                    Already have one?{' '}
                    <button onClick={onLogin} style={{ background: 'none', border: 'none', color: '#6b6b75', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                      Sign in
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}


        {/* ══ GEX HEATMAP VIEW ══ */}
        {navTab === 'heatmap' && (
          <div style={{ animation: 'gl-float-up 0.4s ease both' }}>
            <GEXHeatmap
              currentTicker={ticker}
              onSelectTicker={(t) => { setNavTab('dashboard'); fetchGEX(t); }}
              initialFilter={heatmapFilter}
              onBack={() => setNavTab('dashboard')}
            />
          </div>
        )}

      </main>
    </div>
  );
}

function MTag({ c, children }) {
  return <span style={{ marginLeft: '6px', fontSize: '7px', fontWeight: '900', color: c, background: `${c}12`, border: `1px solid ${c}25`, padding: '1px 5px', borderRadius: '3px', letterSpacing: '0.5px', fontFamily: 'monospace' }}>{children}</span>;
}

function fmt(n) {
  if (n === null || n === undefined) return '—';
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toFixed(0);
}

function formatLargeNum(n) {
  if (n === null || n === undefined) return 'N/A';
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 100e6) return `${(n / 1e6).toFixed(0)}M`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toFixed(0);
}
